import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users as UsersIcon, User, MapPin, Plus, Loader2, Trash2, Search } from "lucide-react";
import DataTable from "@/components/DataTable";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AsideSection } from "@/components/AsideSection";
import AddressLookup from "@/components/AddressLookup";
import { toast } from "sonner";
import { MarketingToggles } from "@/components/MarketingSubscriptions";
import { Megaphone } from "lucide-react";

const columns = [
  { key: "legacy_id", label: "ID", mono: true },
  { key: "full_name", label: "Full Name", strong: true, truncate: true },
  { key: "company", label: "Company", truncate: true },
  { key: "email", label: "Email", truncate: true },
  { key: "telephone", label: "Telephone" },
  { key: "mobile", label: "Mobile" },
  { key: "postcode", label: "Postcode", mono: true },
  { key: "address", label: "Address", truncate: true },
];

const EMPTY = { full_name: "", email: "", telephone: "", mobile: "", company: "", job_title: "", user_type: "Customer", notes: "", addresses: [], whatsapp_subscribed: false, newsletter_subscribed: false };

const yesNo = [{ value: "yes", label: "Subscribed" }, { value: "no", label: "Not subscribed" }];
const filters = [
  { param: "whatsapp_subscribed", label: "WhatsApp", type: "select", options: yesNo },
  { param: "newsletter_subscribed", label: "Newsletter", type: "select", options: yesNo },
];

function ExistingAddressPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/contacts/addresses/search", { params: { q } })
        .then((r) => { setResults(r.data.items || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Use an existing address — search by street or postcode…" className="pl-8 h-9" data-testid="existing-address-search" />
      </div>
      {open && q.trim().length >= 2 && (
        <div className="mt-1 rounded-lg border border-slate-200 bg-white shadow-sm max-h-56 overflow-y-auto" data-testid="existing-address-results">
          {loading ? (
            <div className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">No matching addresses found.</div>
          ) : results.map((a, i) => (
            <button key={i} type="button" onClick={() => { onPick(a); setQ(""); setResults([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`existing-address-${i}`}>
              <div className="text-sm text-slate-800 truncate">{[a.label, a.line1, a.city, a.postcode].filter(Boolean).join(" · ") || a.postcode}</div>
              <div className="text-[11px] text-slate-400">{a.source === "job" ? "From a linked job venue" : "From an existing customer"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UsersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canCreate = can("customers.edit");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (i, k, v) => setForm((f) => { const a = [...f.addresses]; a[i] = { ...a[i], [k]: v }; return { ...f, addresses: a }; });
  const addAddr = () => setForm((f) => ({ ...f, addresses: [...f.addresses, { label: "", line1: "", line2: "", city: "", postcode: "", country: "" }] }));
  const rmAddr = (i) => setForm((f) => ({ ...f, addresses: f.addresses.filter((_, x) => x !== i) }));
  const pickAddr = (a) => {
    setForm((f) => ({ ...f, addresses: [...f.addresses, { label: a.label || "", line1: a.line1 || "", line2: a.line2 || "", city: a.city || "", postcode: a.postcode || "", country: a.country || "United Kingdom" }] }));
    toast.success("Address added from the database");
  };
  const onLookup = (a) => setForm((f) => ({ ...f, addresses: [...f.addresses, { label: "", line1: a.address_line_1 || "", line2: a.address_line_2 || a.county || "", city: a.town_city || "", postcode: a.postcode || "", country: a.country || "United Kingdom" }] }));

  const openNew = () => { setForm(EMPTY); setOpen(true); };
  const [urlParams, setUrlParams] = useSearchParams();
  useEffect(() => {
    if (urlParams.get("new") && canCreate) { openNew(); setUrlParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate]);
  const save = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/contacts", form);
      toast.success("Customer created");
      setOpen(false); setForm(EMPTY);
      navigate(`/users/${r.data.legacy_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create customer"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <DataTable
        dataset="contacts"
        title="Customers"
        icon={UsersIcon}
        columns={columns}
        params={{ customers_only: true }}
        filters={filters}
        searchPlaceholder="Search name, email, ID…"
        showContactsNote
        rowLink={(r) => `/users/${r.legacy_id}`}
        headerActions={canCreate ? (
          <Button onClick={openNew} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="customer-add-btn">
            <Plus className="w-4 h-4" /> New customer
          </Button>
        ) : null}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-create-dialog">
          <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
          <AsideSection first icon={User} title="Contact details" description="Name, contact channels and notes for this new customer.">
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full name</span>
              <Input value={form.full_name} onChange={(e) => setF("full_name", e.target.value)} className="mt-1" data-testid="customer-create-name" placeholder="e.g. Jane Smith" /></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</span>
                <Input value={form.email} onChange={(e) => setF("email", e.target.value)} className="mt-1" data-testid="customer-create-email" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</span>
                <Input value={form.user_type} onChange={(e) => setF("user_type", e.target.value)} className="mt-1" data-testid="customer-create-type" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Telephone</span>
                <Input value={form.telephone} onChange={(e) => setF("telephone", e.target.value)} className="mt-1" data-testid="customer-create-telephone" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile</span>
                <Input value={form.mobile} onChange={(e) => setF("mobile", e.target.value)} className="mt-1" data-testid="customer-create-mobile" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</span>
                <Input value={form.company} onChange={(e) => setF("company", e.target.value)} className="mt-1" data-testid="customer-create-company" /></label>
              <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job title</span>
                <Input value={form.job_title} onChange={(e) => setF("job_title", e.target.value)} className="mt-1" data-testid="customer-create-jobtitle" /></label>
            </div>
            <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</span>
              <textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} rows={2}
                className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="customer-create-notes" /></label>
          </AsideSection>
          <AsideSection icon={Megaphone} title="Marketing subscriptions" description="Both start as not subscribed. Tick only if the customer agreed.">
            <MarketingToggles prefix="customer-create" wa={form.whatsapp_subscribed} newsletter={form.newsletter_subscribed} onChange={setF} />
          </AsideSection>

          <AsideSection icon={MapPin} title="Addresses" description="Optional postal addresses for this customer. Reuse an address already in the system, or add a new one. You can add more later.">
            <div className="space-y-3">
              <AddressLookup onSelect={onLookup} />
              <ExistingAddressPicker onPick={pickAddr} />
              {form.addresses.map((a, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`customer-create-address-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Label (e.g. Home, Site)" value={a.label || ""} onChange={(e) => setAddr(i, "label", e.target.value)} className="h-8" data-testid={`caddr-label-${i}`} />
                    <button onClick={() => rmAddr(i)} className="text-rose-500 hover:bg-rose-50 rounded p-1.5 shrink-0" data-testid={`caddr-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <Input placeholder="Address line 1" value={a.line1 || ""} onChange={(e) => setAddr(i, "line1", e.target.value)} className="h-8" data-testid={`caddr-line1-${i}`} />
                  <Input placeholder="Address line 2" value={a.line2 || ""} onChange={(e) => setAddr(i, "line2", e.target.value)} className="h-8" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" value={a.city || ""} onChange={(e) => setAddr(i, "city", e.target.value)} className="h-8" />
                    <Input placeholder="Postcode" value={a.postcode || ""} onChange={(e) => setAddr(i, "postcode", e.target.value)} className="h-8" data-testid={`caddr-postcode-${i}`} />
                    <Input placeholder="Country" value={a.country || ""} onChange={(e) => setAddr(i, "country", e.target.value)} className="h-8" />
                  </div>
                </div>
              ))}
              <Button variant="outline" className="gap-1.5 w-full border-dashed" onClick={addAddr} data-testid="caddr-add"><Plus className="w-4 h-4" /> Add address</Button>
            </div>
          </AsideSection>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={saving} onClick={save} data-testid="customer-create-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Create customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
